import { StatusEnum } from 'enums/StatusEnum';
import i18next, { t } from 'i18next';
import { type AssignedUser } from 'interfaces/activityLog/ActivityLog.interface';
import { type FC, type ReactNode } from 'react';
import Avatar from 'react-avatar';
import { Trans, useTranslation } from 'react-i18next';
import { stringToCapitalize } from 'utils/formatters/string/stringFormatter';
import { getStatusReadableName } from 'utils/statusUtil/statusUtil';

const AssignedToUserAvatar: FC<{
  children?: React.ReactNode;
  assignedFrom?: string;
  assignedTo?: string;
}> = ({ children, assignedFrom, assignedTo }) => {
  const { t } = useTranslation();
  return (
    <span>
      {assignedFrom === assignedTo ? (
        <span>{t('anomaliesPage.activityLog.himself')}</span>
      ) : (
        <div className="inline-flex items-baseline gap-1 ml-0.5">
          <Avatar
            name={assignedTo ?? '*'}
            className="rounded-full cursor-pointer z-10 self-center"
            textSizeRatio={1.8}
            size={'18px'}
          />
          <span className="font-bold">{assignedTo}</span>
        </div>
      )}
    </span>
  );
};

const createActivityLogComment = ({
  fieldName,
  assignedFrom,
  type,
}: {
  fieldName: string;
  assignedFrom: AssignedUser;
  type?: 'default' | 'min';
}): ReactNode => {
  if (fieldName === 'activity') {
    return type === 'default' ? (
      <p>
        {i18next.t('anomaliesPage.activityLog.newComment', {
          name: assignedFrom?.name as string,
        })}
      </p>
    ) : (
      <p>{i18next.t('anomaliesPage.activityLog.newComment-min')}</p>
    );
  }
  return null;
};

const createRootCauseChanged = ({ value }: { value?: string }): ReactNode => (
  <p>
    {i18next.t('anomaliesPage.activityLog.rootCauseChanged')}
    <span className="font-bold">
      {value ? stringToCapitalize(value).replaceAll('_', ' ') : ''}
    </span>
  </p>
);

const createLessonsLearnedChanged = ({
  value,
}: {
  value?: string;
}): ReactNode => (
  <p>
    {i18next.t('anomaliesPage.activityLog.lessonsLearnedChanged')}
    <span className="font-bold">{value}</span>
  </p>
);

const createStatusUpdated = ({
  assignedFrom,
  assignedTo,
  value,
  type = 'default',
  createdAt,
}: {
  assignedFrom: AssignedUser;
  assignedTo: AssignedUser;
  value?: string;
  type?: 'default' | 'min';
  createdAt?: string;
}): ReactNode => {
  const status = getStatusReadableName(
    StatusEnum[value as keyof typeof StatusEnum]
  );

  // Check if the log was created before October 15, 2025
  const cutoffDate = new Date('2025-10-15');
  const logDate = createdAt ? new Date(createdAt) : new Date();
  const useOldFormat = logDate < cutoffDate;

  if (useOldFormat) {
    // Use the old format that includes both status change and assignment
    return type === 'default' ? (
      <p>
        <Trans
          i18nKey="anomaliesPage.activityLog.statusUpdated_old"
          values={{
            name: assignedFrom.name,
            status,
            assignedTo: assignedTo.name,
          }}
          components={{
            0: <span className="font-bold" />,
            1: <span className="font-bold" />,
            2: (
              <AssignedToUserAvatar
                assignedFrom={assignedFrom.name}
                assignedTo={assignedTo.name}
              />
            ),
          }}
        />
      </p>
    ) : (
      <p>
        {t('anomaliesPage.activityLog.statusUpdated-mini')}
        <span className="font-bold">{status}</span>
      </p>
    );
  }

  // Use the new format (status change only)
  return type === 'default' ? (
    <p>
      <Trans
        i18nKey="anomaliesPage.activityLog.statusUpdated"
        values={{ name: assignedFrom.name, status }}
        components={{
          0: <span className="font-bold" />,
          1: <span className="font-bold" />,
        }}
      />
    </p>
  ) : (
    <p>
      {t('anomaliesPage.activityLog.statusUpdated-mini')}
      <span className="font-bold">{status}</span>
    </p>
  );
};

const createAssignedToUpdated = ({
  assignedFrom,
  assignedTo,
  type = 'default',
}: {
  assignedFrom: AssignedUser;
  assignedTo: AssignedUser;
  value?: string;
  type?: 'default' | 'min';
}): ReactNode => {
  return type === 'default' ? (
    <p>
      <Trans
        i18nKey="anomaliesPage.activityLog.assignedUser"
        values={{ name: assignedFrom.name, assignedTo: assignedTo.name }}
        components={{
          0: <span className="font-bold" />,
          1: (
            <AssignedToUserAvatar
              assignedFrom={assignedFrom.name}
              assignedTo={assignedTo.name}
            />
          ),
        }}
      />
    </p>
  ) : (
    <p>
      <span className="font-bold">{assignedFrom.name}</span>
      {' assigned to '}
      <AssignedToUserAvatar
        assignedFrom={assignedFrom.name}
        assignedTo={assignedTo.name}
      />
    </p>
  );
};

export const createLog = ({
  fieldName,
  assignedFrom,
  assignedTo,
  title,
  value,
  type = 'default',
  createdAt,
}: {
  fieldName: string;
  assignedFrom: AssignedUser;
  assignedTo: AssignedUser;
  title: string;
  value?: string;
  type?: 'default' | 'min';
  createdAt?: string;
}): ReactNode => {
  switch (fieldName) {
    case 'activity':
      return createActivityLogComment({ fieldName, assignedFrom, type });
    case 'root_cause':
      return createRootCauseChanged({ value });
    case 'lessons_learned':
      return createLessonsLearnedChanged({ value });
    case 'status':
      return createStatusUpdated({
        assignedFrom,
        assignedTo,
        value,
        type,
        createdAt,
      });
    case 'assignment':
      return createAssignedToUpdated({ assignedFrom, assignedTo, value, type });
    default:
      return title;
  }
};
